// src/portal-app/shell/kit/CondensedRow.js
//
// THE core list row. Every list surface in the redesigned app renders these.
//
// Two-row structure, and the indent on row 2 is the whole point:
//   row 1 — icon · name ······ value · chevron
//   row 2 —       secondary ······ pill        (indented 44px, never wraps)
//
// The pre-redesign card put the name under the icon and the value in a middle
// column, which squeezed the secondary text into a narrow gutter where it
// wrapped three lines deep and pushed cards to ~110px. This layout is ~78px
// and carries the same information. See spec §1 complaint 3 and §6.
import React from 'react';
import { GlassCard } from '../LiquidGlass';
import { ensureKitCss } from './kitCss';
import { IconChevron } from './icons';
import { toneVar } from './tones';

export function CondensedRow({
    icon,
    tone = 'blue',
    name,
    value,
    secondary,
    pill,
    onPress,
    testId,
}) {
    ensureKitCss();

    const tappable = typeof onPress === 'function';
    const token = toneVar(tone);
    const hasBottom = Boolean(secondary) || Boolean(pill);

    // Screen readers get one coherent label instead of four disconnected
    // nodes — and it must include the pill: colour is never the sole carrier
    // of meaning (spec §9), so an exception pill's text has to reach the
    // accessible name, not just the visual row. aria-label on a <button>
    // replaces its content in the accessibility tree, so anything not folded
    // in here is invisible to a screen reader despite being on screen.
    const label = [name, value != null && value !== '' ? value : null, secondary || null, pill?.label || null]
        .filter((part) => part != null && part !== '')
        .join(', ');

    const cls = ['pk-row', tappable && 'pk-row-tappable'].filter(Boolean).join(' ');

    return (
        <GlassCard
            as={tappable ? 'button' : 'div'}
            lift={tappable}
            className={cls}
            onClick={tappable ? onPress : undefined}
            type={tappable ? 'button' : undefined}
            aria-label={tappable ? label : undefined}
            data-testid={testId ? `condensed-row-${testId}` : undefined}
        >
            <div className="pk-row-top">
                <span
                    className="pk-isq"
                    style={{
                        width: 32,
                        height: 32,
                        background: `color-mix(in srgb, var(${token}) 11%, transparent)`,
                        color: `var(${token})`,
                    }}
                    aria-hidden="true"
                >
                    {icon}
                </span>
                <span className="pk-row-name" title={name || undefined}>{name}</span>
                <span className="pk-row-spacer" />
                {value != null && value !== '' && (
                    <span className="pk-row-value">{value}</span>
                )}
                {tappable && (
                    <span className="pk-chev" aria-hidden="true"><IconChevron /></span>
                )}
            </div>

            {hasBottom && (
                <div className="pk-row-bottom">
                    {secondary && <span className="pk-row-sec" title={secondary}>{secondary}</span>}
                    <span className="pk-row-spacer" />
                    {pill && (
                        <span className={`pk-pill pk-pill-${pill.tone || 'muted'}`}>
                            {pill.label}
                        </span>
                    )}
                </div>
            )}
        </GlassCard>
    );
}

export default CondensedRow;
